import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet, ArrowLeft, Loader2, Info, Truck, UserCog, Coins } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';
import { DriverPayEditorSheet } from '@/components/payments/DriverPayEditorSheet';
import { BillingRatesSheet } from '@/components/payments/BillingRatesSheet';
import { ReportSheet } from '@/components/payments/ReportSheet';
import { VehicleDetailSheet, type VehicleRow as VRow, type Focus } from '@/components/payments/VehicleDetailSheet';
import { DriverDetailSheet } from '@/components/payments/DriverDetailSheet';

// PR-NEOM-4 + P1 + P2 — Payments: Vehicles P&L and Drivers pay. Salary from the
// pay model (lib/neom-pay). Every number opens its exact calculation.

const SCOPE: Record<string, string> = {
  'PRJ-NEOM': 'neom', 'PRJ-RIYADH': 'riyadh', 'PRJ-DUMPERS': 'dumpers',
  'PRJ-RIYADH-WATER': 'riyadh_water',
};
type View = 'vehicles' | 'drivers';

interface VehicleRow extends VRow { investorId: string | null; driverName: string | null }
interface Totals { trips: number; revenueSar: number; driverSalarySar: number; maintenanceSar: number; dieselSar: number; netSar: number; investorPayoutSar: number }
interface VehiclePnl { scope: string; month: string; items: VehicleRow[]; totals: Totals }

interface DriverPay {
  driverId: string; driverCode: string | null; driverName: string | null;
  payModel: string; source: string; tripsCount: number;
  components: { base: number; perTrip: number; perKm: number; bonus: number };
  gross: number; deductions: { advances: number; reconciledExpenses: number }; netPayable: number;
}
interface DriverPayList { from: string; to: string; items: DriverPay[] }

const MODEL_LABEL: Record<string, string> = { fixed: 'Fixed', base_plus_per_trip: 'Base + per trip', base_plus_per_km: 'Base + per km', target_with_bonus: 'Target + bonus' };
const sar = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: 0 }));
const neg = (n: number) => (n > 0 ? '- ' + sar(n) : '0');

export default function PaymentsPage() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { project } = useCurrentProject();
  const scope = project?.code ? SCOPE[project.code] : null;
  const qc = useQueryClient();
  const [view, setView] = useState<View>('vehicles');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rangeMode, setRangeMode] = useState<'month' | 'custom'>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const range = (() => {
    if (rangeMode === 'custom' && customFrom && customTo) return { from: customFrom, to: customTo, label: `${customFrom} → ${customTo}` };
    const [y, m] = month.split('-').map(Number);
    const from = `${y}-${String(m).padStart(2, '0')}-01`;
    const to = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    return { from, to, label: month };
  })();
  const editorMonth = range.from.slice(0, 7);
  const [detailVehicle, setDetailVehicle] = useState<{ row: VehicleRow; focus: Focus } | null>(null);
  const [detailDriver, setDetailDriver] = useState<{ id: string; name: string | null } | null>(null);
  const [editDriver, setEditDriver] = useState<{ id: string; name: string | null } | null>(null);
  const [showBilling, setShowBilling] = useState(false);
  const [report, setReport] = useState<{ kind: 'vehicle' | 'driver'; id: string } | null>(null);

  const veh = useQuery({
    queryKey: ['vehicle-pnl', scope, range.from, range.to], enabled: !!scope && view === 'vehicles',
    queryFn: async () => (await apiClient.get<VehiclePnl>('/api/v1/project-reports/vehicle-pnl', { params: { scope, from: range.from, to: range.to } })).data,
  });
  const drv = useQuery({
    queryKey: ['neom-pay', range.from, range.to], enabled: !!scope && view === 'drivers',
    queryFn: async () => (await apiClient.get<DriverPayList>('/api/v1/driver-finance/neom-pay', { params: { from: range.from, to: range.to } })).data,
  });

  const afterEdit = () => {
    setEditDriver(null);
    qc.invalidateQueries({ queryKey: ['neom-pay'] });
    qc.invalidateQueries({ queryKey: ['neom-pay-one'] });
    qc.invalidateQueries({ queryKey: ['vehicle-pnl'] });
    qc.invalidateQueries({ queryKey: ['vehicle-detail'] });
  };

  const th = 'px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium';
  // A number that opens its breakdown.
  const Num = ({ row, focus, children, className = '' }: { row: VehicleRow; focus: Focus; children: ReactNode; className?: string }) => (
    <button type="button" onClick={() => setDetailVehicle({ row, focus })}
      className={`hover:underline decoration-dotted underline-offset-2 ${className}`}>{children}</button>
  );

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {rangeMode === 'month' ? (
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
            ) : (
              <span className="inline-flex items-center gap-1">
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
                <span className="text-muted-foreground text-xs">to</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
              </span>
            )}
            <button onClick={() => setRangeMode(rangeMode === 'month' ? 'custom' : 'month')}
              className="h-9 rounded-md border border-border px-2 text-xs text-muted-foreground hover:text-foreground" title="Switch between a whole month and a custom date range">
              {rangeMode === 'month' ? 'Custom range' : 'Month'}
            </button>
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className="h-3 w-3" /> Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <Wallet className="h-7 w-7 text-emerald-500" />
              <h1 className="font-display text-3xl text-foreground">Payments</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{range.label}: click any number to see exactly how it is made.</p>
          </div>
          <div className="flex items-center gap-2">
            {scope === 'neom' && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowBilling(true)}>
                <Coins className="h-4 w-4" /> Billing rates
              </Button>
            )}
            <div className="inline-flex rounded-lg border border-border bg-card p-1">
              <button onClick={() => setView('vehicles')}
                className={'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' + (view === 'vehicles' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                <Truck className="h-4 w-4" /> Vehicles
              </button>
              <button onClick={() => setView('drivers')}
                className={'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' + (view === 'drivers' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                <UserCog className="h-4 w-4" /> Drivers
              </button>
            </div>
          </div>
        </div>

        {!scope ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <Info className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <div className="text-lg font-medium">Pick a project first</div>
            <div className="text-sm text-muted-foreground mt-1 mb-4">Payments are per project. Open NEOM from the home.</div>
            <Link to="/home"><Button>Go to home</Button></Link>
          </div>
        ) : view === 'vehicles' ? (
          veh.isLoading ? <Spinner /> : veh.isError ? <ErrLine /> : !veh.data || veh.data.items.length === 0 ? (
            <Empty msg="No active vehicles for this project." />
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      {['Vehicle', 'Driver', 'Trips', 'Rate', 'Revenue', 'Salary', 'Maint', 'Diesel', 'Net', 'Investor payout'].map((h, i) => (
                        <th key={h} className={`${th} ${i < 2 ? 'text-start' : 'text-end'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {veh.data.items.map((r) => (
                      <tr key={r.vehicleId} className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => setDetailVehicle({ row: r, focus: 'net' })} className="font-mono text-xs hover:underline" dir="ltr">{r.plateEn ?? r.code ?? '—'}</button>
                        </td>
                        <td className="px-4 py-3">{r.driverName ?? <span className="text-muted-foreground italic">—</span>}</td>
                        <td className="px-4 py-3 text-end tabular-nums">{r.trips}</td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground"><Num row={r} focus="revenue">{sar(r.rateSar)}</Num></td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums"><Num row={r} focus="revenue">{sar(r.revenueSar)}</Num></td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground"><Num row={r} focus="salary">{neg(r.driverSalarySar)}</Num></td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground"><Num row={r} focus="maint">{neg(r.maintenanceSar)}</Num></td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground"><Num row={r} focus="diesel">{neg(r.dieselSar)}</Num></td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums font-semibold">
                          <Num row={r} focus="net" className={r.netSar >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>{sar(r.netSar)}</Num>
                        </td>
                        <td className="px-4 py-3 text-end font-mono tabular-nums">
                          {r.investorName ? <Num row={r} focus="payout"><span title={r.investorName}>{sar(r.investorPayoutSar)}</span></Num> : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/40 border-t border-border font-medium">
                    <tr>
                      <td className="px-4 py-3" colSpan={2}>Total ({veh.data.items.length} vehicles)</td>
                      <td className="px-4 py-3 text-end tabular-nums">{veh.data.totals.trips}</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-end font-mono tabular-nums">{sar(veh.data.totals.revenueSar)}</td>
                      <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">{neg(veh.data.totals.driverSalarySar)}</td>
                      <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">{neg(veh.data.totals.maintenanceSar)}</td>
                      <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">{neg(veh.data.totals.dieselSar)}</td>
                      <td className={`px-4 py-3 text-end font-mono tabular-nums ${veh.data.totals.netSar >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{sar(veh.data.totals.netSar)}</td>
                      <td className="px-4 py-3 text-end font-mono tabular-nums">{veh.data.totals.investorPayoutSar > 0 ? sar(veh.data.totals.investorPayoutSar) : '—'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">Click a number for its breakdown. Net = revenue − salary − maintenance − diesel.</div>
            </div>
          )
        ) : (
          drv.isLoading ? <Spinner /> : drv.isError ? <ErrLine /> : !drv.data || drv.data.items.length === 0 ? (
            <Empty msg="No active NEOM drivers." />
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      {['Driver', 'Pay model', 'Trips', 'Base', 'Variable', 'Gross', 'Deductions', 'Net pay'].map((h, i) => (
                        <th key={h} className={`${th} ${i < 2 ? 'text-start' : 'text-end'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drv.data.items.map((d) => {
                      const variable = d.components.perTrip + d.components.perKm + d.components.bonus;
                      const ded = d.deductions.advances + d.deductions.reconciledExpenses;
                      return (
                        <tr key={d.driverId} onClick={() => setDetailDriver({ id: d.driverId, name: d.driverName })}
                          className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer">
                          <td className="px-4 py-3 font-medium">{d.driverName ?? d.driverCode ?? '—'}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs">{MODEL_LABEL[d.payModel] ?? d.payModel}</span>
                            {d.source === 'driver_default' && <span className="ms-1 text-[10px] text-amber-600 dark:text-amber-400" title="No pay model set yet — using default">default</span>}
                          </td>
                          <td className="px-4 py-3 text-end tabular-nums">{d.tripsCount}</td>
                          <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">{sar(d.components.base)}</td>
                          <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">{variable > 0 ? sar(variable) : '—'}</td>
                          <td className="px-4 py-3 text-end font-mono tabular-nums">{sar(d.gross)}</td>
                          <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">{neg(ded)}</td>
                          <td className="px-4 py-3 text-end font-mono tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">{sar(d.netPayable)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">Click a driver to see how the salary was built and to set the pay model.</div>
            </div>
          )
        )}
      </main>

      {detailVehicle && scope && (
        <VehicleDetailSheet vehicle={detailVehicle.row} scope={scope} from={range.from} to={range.to} label={range.label} focus={detailVehicle.focus}
          onClose={() => setDetailVehicle(null)}
          onReport={() => { const vid = detailVehicle.row.vehicleId; setDetailVehicle(null); setReport({ kind: 'vehicle', id: vid }); }} />
      )}
      {detailDriver && (
        <DriverDetailSheet driverId={detailDriver.id} driverName={detailDriver.name} from={range.from} to={range.to} label={range.label}
          onClose={() => setDetailDriver(null)} onEdit={() => { const d = detailDriver; setDetailDriver(null); setEditDriver(d); }}
          onReport={() => { const d = detailDriver; setDetailDriver(null); setReport({ kind: 'driver', id: d.id }); }} />
      )}
      {report && <ReportSheet kind={report.kind} id={report.id} from={range.from} to={range.to} onClose={() => setReport(null)} />}
      {editDriver && (
        <DriverPayEditorSheet driverId={editDriver.id} driverName={editDriver.name} month={editorMonth}
          onClose={() => setEditDriver(null)} onSaved={afterEdit} />
      )}
      {showBilling && scope && <BillingRatesSheet scope={scope} onClose={() => setShowBilling(false)} />}

      <footer className="py-6 text-center text-xs text-muted-foreground border-t border-border">© Vida Group · Saudi Arabia</footer>
    </div>
  );
}

function Spinner() { return <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>; }
function ErrLine() { return <div className="py-16 text-center text-sm text-rose-600 dark:text-rose-400">Could not load.</div>; }
function Empty({ msg }: { msg: string }) { return <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">{msg}</div>; }
